import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowDecision, FlowNote } from '../FlowPrimitives';

// ============================================================================
// Experiment 6 – Vitamin C Analysis (Iodometric Titration)
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp6',
  title: 'Iodometric Determination of Ascorbic Acid in Vitamin C Tablets',
  objectives: [
    'Determine Vitamin C content in commercial tablets',
    'Apply redox titration techniques',
    'Use starch–iodine endpoint detection',
    'Evaluate compliance with USP standards (90–110% of label)',
  ],
  safetyNotes: [
    'Iodine (0.10 N I₂) solution stains skin and clothing',
    '2 N H₂SO₄ is corrosive – handle with care',
    'Use freshly boiled and cooled water (oxygen-free) to prevent vitamin C oxidation',
    'Starch test solution should be available',
  ],
  equipment: [
    'Burette (50 mL)',
    'Erlenmeyer flasks (4, labelled: rapid titration, sample 1, 2, 3)',
    'Mortar and pestle',
    'Analytical balance (to 0.0001 g)',
    'Timer (90-second validation)',
  ],
  chemicals: [
    '0.10 N iodine (I₂) solution',
    'Starch test solution (3 mL per flask)',
    '2 N H₂SO₄ (25 mL per flask)',
    'Oxygen-free (boiled) water (100 mL per flask)',
    'Vitamin C tablets (record all info from bottle)',
  ],
  procedure: [
    {
      number: 1,
      title: 'Determine Average Tablet Mass',
      details: [
        'Count and weigh 10 tablets together',
        'Calculate average tablet mass',
      ],
      manualRef: 'Part A',
    },
    {
      number: 2,
      title: 'Prepare Powder Samples',
      details: [
        'Grind 3–5 tablets to a fine powder using mortar and pestle',
        'Weigh four portions of approximately 400 mg each',
        'Record the exact mass of each sample',
      ],
      manualRef: 'Part A',
    },
    {
      number: 3,
      title: 'Dissolve Sample',
      details: [
        'Transfer one sample to a 250 mL Erlenmeyer flask',
        'Add 100 mL of oxygen-free (boiled) water',
        'Swirl to dissolve',
        'Add 25 mL of 2 N H₂SO₄',
      ],
      safetyNote: '2 N H₂SO₄ is corrosive – add carefully',
      manualRef: 'Part B',
    },
    {
      number: 4,
      title: 'Titrate with Iodine',
      details: [
        'Fill burette with 0.10 N I₂ solution',
        'Record initial volume',
        'Perform one blank titration first',
        'Titrate rapidly until solution becomes pale yellow',
        'Add 3 mL starch test solution',
        'Continue titrating dropwise',
      ],
      safetyNote: 'Iodine stains – wipe spills immediately',
      manualRef: 'Part B',
    },
    {
      number: 5,
      title: 'Confirm Endpoint (90-Second Rule)',
      details: [
        'Endpoint = dirty purple color that persists for at least 90 seconds',
        'If color fades before 90 s, add more I₂ dropwise and re-time',
        'True endpoint = first permanent dirty purple',
        'Record final burette volume',
      ],
      manualRef: 'Part B',
    },
    {
      number: 6,
      title: 'Repeat for Remaining Samples',
      details: [
        'Repeat Steps 3–5 for each of the four weighed portions',
        'Record all volumes and masses',
      ],
      manualRef: 'Part B',
    },
    {
      number: 7,
      title: 'Calculate Vitamin C Content',
      details: [
        'mg ascorbic acid = mL I₂ × 8.806 (for 0.10 N I₂)',
        'mg per tablet = (mg in sample × avg tablet mass) / sample mass',
        '% of label = (actual mg / labeled mg) × 100',
        'USP requirement: 90–110% of labeled amount',
      ],
      manualRef: 'Calculations',
    },
  ],
  keyFormulas: [
    'C₆H₈O₆ + I₂ → C₆H₆O₆ + 2HI',
    'mg ascorbic acid = mL I₂ × 8.806',
    'mg per tablet = (mg in sample × avg tablet mass) / sample mass',
    '% of label = (actual mg / labeled mg) × 100',
  ],
  tips: [
    'Use oxygen-free water to prevent premature oxidation of ascorbic acid',
    'Add starch indicator near the endpoint (pale yellow), not at the start',
    'The endpoint is a dirty purple, not a deep blue-black',
    'Approach the endpoint dropwise and wait between drops',
    'Use a timer for the 90-second persistence check',
    'Iodine in the burette may slowly deposit – rinse burette with I₂ solution before use',
    'Grind tablets thoroughly for consistent sample portions',
  ],
};

// ============================================================================
// Flowchart
// ============================================================================

function Flowchart() {
  // Layout constants
  const cx = 210;          // center x for nodes (600/2 - 180/2)
  const nodeW = 180;
  const nodeH = 56;
  const termW = 140;
  const termH = 36;
  const decisionSize = 60;

  // Vertical positions
  const startY = 40;
  const n1Y = 90;          // Prep Samples
  const n2Y = 164;         // Dissolve Vitamin C
  const n3Y = 238;         // Titrate with Iodine
  const n4Y = 312;         // Record Volume
  const dY = 386;          // Decision: 90 s persists?
  const n5Y = 476;         // Calculate mg Vit C
  const endY = 550;

  return (
    <FlowContainer width={600} height={600} title="Vitamin C Iodometric Titration Flow">
      {/* Start */}
      <FlowTerminal x={cx + (nodeW - termW) / 2} y={startY} label="Start" />

      {/* Arrow: Start -> Prep */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: startY + termH }}
        to={{ x: cx + nodeW / 2, y: n1Y }}
      />

      {/* Step 1: Prep Samples */}
      <FlowNode x={cx} y={n1Y} title="Prep Samples" subtitle="Grind tablets, weigh ~400 mg" />

      {/* Arrow */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n1Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: n2Y }}
      />

      {/* Step 2: Dissolve */}
      <FlowNode x={cx} y={n2Y} title="Dissolve Vitamin C" subtitle="100 mL O₂-free H₂O + 25 mL 2N H₂SO₄" color="#2563eb" />

      {/* Arrow */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n2Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: n3Y }}
      />

      {/* Step 3: Titrate */}
      <FlowNode x={cx} y={n3Y} title="Titrate with I₂" subtitle="Rapid → pale yellow → dropwise" color="#7c3aed" />

      {/* Note about starch */}
      <FlowNote x={20} y={n3Y + 6} text="Add 3 mL starch at pale yellow" width={160} />

      {/* Arrow */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n3Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: n4Y }}
      />

      {/* Step 4: Record Volume */}
      <FlowNode x={cx} y={n4Y} title="Record Volume" subtitle="Note dirty purple color" />

      {/* Arrow to Decision */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n4Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: dY }}
      />

      {/* Decision: 90 seconds? */}
      <FlowDecision
        x={cx + nodeW / 2 - decisionSize / 2}
        y={dY}
        size={decisionSize}
        label="90 s?"
      />

      {/* No branch: loop back to Titrate */}
      <FlowArrow
        from={{ x: cx + nodeW / 2 + decisionSize / 2, y: dY + decisionSize / 2 }}
        to={{ x: cx + nodeW + 30, y: dY + decisionSize / 2 }}
        label="Fades"
        color="#ef4444"
      />
      <FlowArrow
        from={{ x: cx + nodeW + 30, y: dY + decisionSize / 2 }}
        to={{ x: cx + nodeW + 30, y: n3Y + nodeH / 2 }}
        color="#ef4444"
        dashed
      />
      <FlowArrow
        from={{ x: cx + nodeW + 30, y: n3Y + nodeH / 2 }}
        to={{ x: cx + nodeW, y: n3Y + nodeH / 2 }}
        label="Add more I₂"
        color="#ef4444"
      />

      {/* Yes branch: continue */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: dY + decisionSize }}
        to={{ x: cx + nodeW / 2, y: n5Y }}
        label="Persists"
      />

      {/* Step 5: Calculate */}
      <FlowNode x={cx} y={n5Y} title="Calculate mg Vit C" subtitle="mL I₂ × 8.806 = mg" color="#0d9488" />

      {/* Arrow to End */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n5Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: endY }}
      />

      {/* End */}
      <FlowTerminal x={cx + (nodeW - termW) / 2} y={endY} label="End" color="#ef4444" />

      {/* USP note */}
      <FlowNote x={20} y={n5Y + 6} text="USP: 90–110% of label" width={150} />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp6Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
